"use client";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useProfile } from "@/hooks/use-profile";
import { useOwnedStores } from "@/hooks/use-owned-stores";
import { useRouter } from "next/navigation";

interface PlanFeature {
  icon: string;
  text: string;
}

interface PricingPlan {
  id: string;
  badge?: string;
  badgeVariant?:
    | "primary"
    | "destructive"
    | "outline"
    | "secondary"
    | "success"
    | "warning";
  title: string;
  price: string;
  subPrice?: string; // original price for annual
  subLabel?: string; // e.g. One-time payment
  highlightNote?: string; // e.g. Save $58 per year!
  cta: string;
  features: PlanFeature[];
  footerNote?: string;
}

const plans: PricingPlan[] = [
  {
    id: "monthly",
    badge: "MOST POPULAR",
    badgeVariant: "primary",
    title: "Featured Monthly",
    price: "$29/month",
    cta: "Start Monthly Plan",
    features: [
      {
        icon: "⭐",
        text: "Top placement on your city page - Be the first store customers see",
      },
      {
        icon: "📸",
        text: "Add images - Showcase your storefront and inventory",
      },
      { icon: "✓", text: "5x more visibility than standard listings" },
      { icon: "✓", text: "Featured badge to stand out" },
      { icon: "✓", text: "Priority in search results" },
      { icon: "✓", text: "Cancel anytime, no contracts" },
    ],
  },
  {
    id: "annual",
    badge: "SAVE 16%",
    badgeVariant: "success",
    title: "Featured Annual",
    price: "$290/year",
    subPrice: "$348/year",
    highlightNote: "💰 Save $58 per year!",
    cta: "Save with Annual Plan",
    features: [
      {
        icon: "⭐",
        text: "Top placement on your city page - Be the first store customers see",
      },
      {
        icon: "📸",
        text: "Add images - Showcase your storefront and inventory",
      },
      { icon: "✓", text: "5x more visibility than standard listings" },
      { icon: "✓", text: "Featured badge to stand out" },
      { icon: "✓", text: "Priority in search results" },
      { icon: "🎁", text: "Best value - 2 months FREE!" },
    ],
  },
  {
    id: "lifetime",
    badge: "BEST VALUE",
    badgeVariant: "warning",
    title: "Featured Lifetime",
    price: "$999/lifetime",
    subLabel: "One-time payment",
    highlightNote: "🔥 Pay once, featured forever!",
    cta: "Get Lifetime Access",
    features: [
      {
        icon: "⭐",
        text: "Top placement on your city page - Be the first store customers see",
      },
      {
        icon: "📸",
        text: "Add images - Showcase your storefront and inventory",
      },
      { icon: "✓", text: "5x more visibility than standard listings" },
      { icon: "✓", text: "Featured badge to stand out" },
      { icon: "✓", text: "Priority in search results" },
      { icon: "💎", text: "Featured status FOREVER - Never pay again!" },
    ],
  },
];

export function PricingSection() {
  const { data, isLoading } = useProfile();
  const { data: ownedStoresData, isLoading: storesLoading } = useOwnedStores();
  const router = useRouter();
  const isLoggedIn = !!data?.profile;
  const hasStores = (ownedStoresData?.stores?.length || 0) > 0;

  function handleSelect(planId: string) {
    if (!isLoggedIn) {
      router.push("/signin");
      return;
    }
    if (!hasStores) {
      // Redirect to dashboard onboarding (claim or create listing)
      router.push("/dashboard");
      return;
    }
    // User has at least one store; proceed to upgrade selection
    router.push(`/dashboard/upgrade?plan=${planId}`);
  }

  return (
    <section className="py-24 md:py-32 bg-muted/40 dark:bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-foreground">
            Simple, Transparent <span className="text-primary">Pricing</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            Choose the featured placement that fits your growth goals. No hidden
            fees. Upgrade, downgrade, or cancel anytime.
          </p>
        </motion.div>

        <div className="grid gap-8 md:grid-cols-3 max-w-7xl mx-auto">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
            >
              <Card
                className={`relative h-full flex flex-col p-8 bg-card/90 dark:bg-muted border border-border/60 shadow-sm hover:shadow-md transition-shadow ${
                  plan.id === "monthly" ? "ring-1 ring-primary/40" : ""
                } ${plan.id === "annual" ? "ring-1 ring-green-500/40" : ""} ${
                  plan.id === "lifetime" ? "ring-1 ring-amber-500/40" : ""
                }`}
              >
                {plan.badge && (
                  <span
                    className={`absolute -top-3 left-6 px-3 py-1 text-xs font-semibold rounded-full shadow bg-primary/90 text-primary-foreground ${
                      plan.id === "annual" ? "bg-green-600 text-white" : ""
                    } ${
                      plan.id === "lifetime" ? "bg-amber-600 text-white" : ""
                    }`}
                  >
                    {plan.badge}
                  </span>
                )}
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-2 text-foreground">
                    {plan.title}
                  </h3>
                  <div className="flex flex-col items-start">
                    <span className="text-3xl font-extrabold tracking-tight text-foreground">
                      {plan.price}
                    </span>
                    {plan.subPrice && (
                      <span className="text-sm line-through text-muted-foreground mt-1">
                        {plan.subPrice}
                      </span>
                    )}
                    {plan.subLabel && (
                      <span className="text-sm text-muted-foreground mt-1">
                        {plan.subLabel}
                      </span>
                    )}
                    {plan.highlightNote && (
                      <span className="text-sm font-medium text-primary mt-2">
                        {plan.highlightNote}
                      </span>
                    )}
                  </div>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feat) => (
                    <li
                      key={feat.text}
                      className="flex items-start gap-3 text-left"
                    >
                      <span className="text-lg shrink-0" aria-hidden="true">
                        {feat.icon}
                      </span>
                      <span className="text-sm text-muted-foreground leading-relaxed">
                        {feat.text}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  disabled={isLoading || storesLoading}
                  onClick={() => handleSelect(plan.id)}
                  className={`w-full mt-auto ${
                    plan.id === "monthly"
                      ? ""
                      : plan.id === "annual"
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  }`}
                  variant={plan.id === "monthly" ? "default" : "default"}
                >
                  {isLoading || storesLoading ? "Loading..." : plan.cta}
                </Button>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
